/**
 * Password Validator Utility
 * 
 * Shared password validation logic for all password inputs.
 * Ensures consistent validation rules across registration, 
 * password change, and invitation pages.
 */

import i18n from '../i18n';

/**
 * Password validation result
 */
export interface PasswordValidationResult {
  isValid: boolean;
  error: string | null;
}

/**
 * Validates a password against security requirements:
 * - Minimum 8 characters
 * - At least one uppercase letter (A-Z)
 * - At least one lowercase letter (a-z)
 * - At least one digit (0-9)
 * - At least one special character
 * 
 * @param password - The password to validate
 * @returns Validation result with isValid flag and optional error message
 */
export function validatePassword(password: string): PasswordValidationResult {
  const t = i18n.t.bind(i18n);
  
  if (password.length < 8) {
    return { isValid: false, error: t('auth.register.errors.passwordLength') };
  }
  
  if (!/[A-Z]/.test(password)) {
    return { isValid: false, error: t('auth.register.errors.passwordUppercase') };
  }
  
  if (!/[a-z]/.test(password)) {
    return { isValid: false, error: t('auth.register.errors.passwordLowercase') };
  }
  
  if (!/[0-9]/.test(password)) {
    return { isValid: false, error: t('auth.register.errors.passwordDigit') };
  }
  
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return { isValid: false, error: t('auth.register.errors.passwordSpecial') };
  }
  
  return { isValid: true, error: null };
}

/**
 * Validates that passwords match
 * 
 * @param password - The password
 * @param confirmPassword - The confirmation password
 * @returns Error message if they don't match, null otherwise
 */
export function validatePasswordMatch(password: string, confirmPassword: string): string | null {
  const t = i18n.t.bind(i18n);
  
  if (password !== confirmPassword) {
    return t('auth.register.errors.passwordMismatch');
  }
  
  return null;
}
